import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Globe, 
  Users, 
  DollarSign, 
  Activity,
  Server,
  Shield,
  FileText,
  Settings,
  Plus,
  Search,
  ExternalLink,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  Database,
  CreditCard,
  HelpCircle,
  MessageSquare,
  Lock,
  Unlock,
  Target,
  Ticket,
  Mail,
  UserCog,
  ShieldCheck,
  ShieldX,
  Crown,
  Eye,
  MailCheck,
  Heart,
  Trash2,
  Edit,
  EyeOff
} from 'lucide-react';
import SupportInbox from '../components/SupportInbox';
import OperationsRoadmap from '../components/OperationsRoadmap';
import DevelopmentHistory from '../components/DevelopmentHistory';
import AdRevenueProjections from '../components/VBB/AdRevenueProjections';
import { formatDistanceToNow } from 'date-fns';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== User Management Panel ==============
const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15'
      });
      if (search) params.append('search', search);
      if (filter !== 'all') params.append('filter_role', filter);

      const response = await fetch(`${API_URL}/api/admin/users?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/stats/summary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        toast.success('User updated successfully');
        fetchUsers();
        fetchStats();
        setEditDialogOpen(false);
      } else {
        toast.error('Failed to update user');
      }
    } catch (error) {
      toast.error('Error updating user');
    }
  };

  const handleVerifyEmail = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/verify-email`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Email verified');
        fetchUsers();
        fetchStats();
      } else {
        toast.error('Failed to verify email');
      }
    } catch (error) {
      toast.error('Error verifying email');
    }
  };

  const openEditDialog = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const user = await response.json();
        setSelectedUser(user);
        setEditDialogOpen(true);
      }
    } catch (error) {
      toast.error('Error loading user details');
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-xs text-slate-400">Total Users</p>
            </CardContent>
          </Card>
          <Card className="bg-green-900/30 border-green-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-400">{stats.verified}</p>
              <p className="text-xs text-green-300">Verified</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-900/30 border-amber-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{stats.unverified}</p>
              <p className="text-xs text-amber-300">Unverified</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-900/30 border-purple-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-purple-400">{stats.premium}</p>
              <p className="text-xs text-purple-300">Premium</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-900/30 border-blue-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.paid_members}</p>
              <p className="text-xs text-blue-300">Paid</p>
            </CardContent>
          </Card>
          <Card className="bg-cyan-900/30 border-cyan-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-cyan-400">{stats.admins}</p>
              <p className="text-xs text-cyan-300">Admins</p>
            </CardContent>
          </Card>
          <Card className="bg-pink-900/30 border-pink-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-pink-400">{stats.operators}</p>
              <p className="text-xs text-pink-300">Operators</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <Input
                placeholder="Search by email, username..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
              />
              <Button type="submit" variant="secondary">
                <Search className="w-4 h-4" />
              </Button>
            </form>
            <div className="flex gap-2 flex-wrap">
              {['all', 'admin', 'operator', 'premium', 'paid', 'unverified'].map((f) => (
                <Button
                  key={f}
                  size="sm"
                  variant={filter === f ? 'default' : 'outline'}
                  onClick={() => { setFilter(f); setPage(1); }}
                  className={filter === f ? 'bg-purple-600' : 'border-slate-600 text-slate-300'}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">User</TableHead>
                    <TableHead className="text-slate-400">Email Verified</TableHead>
                    <TableHead className="text-slate-400">Membership</TableHead>
                    <TableHead className="text-slate-400">Roles</TableHead>
                    <TableHead className="text-slate-400">Joined</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className="border-slate-700">
                      <TableCell>
                        <div>
                          <p className="text-white font-medium">{user.username || user.display_name}</p>
                          <p className="text-xs text-slate-400">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.email_verified ? (
                          <Badge className="bg-green-600 text-xs">Verified</Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-500 text-amber-400 text-xs">
                            Unverified
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {user.membership_tier === 'premium' && (
                            <Badge className="bg-purple-600 text-xs">Premium</Badge>
                          )}
                          {user.member_class === 'paid' && (
                            <Badge className="bg-blue-600 text-xs">Paid</Badge>
                          )}
                          {user.beta_membership_active && (
                            <Badge variant="outline" className="border-cyan-500 text-cyan-400 text-xs">Beta</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {user.is_operator_admin && (
                            <Badge className="bg-pink-600 text-xs">Operator</Badge>
                          )}
                          {user.is_sysadmin && (
                            <Badge className="bg-red-600 text-xs">Sysadmin</Badge>
                          )}
                          {user.is_admin && !user.is_sysadmin && !user.is_operator_admin && (
                            <Badge className="bg-amber-600 text-xs">Admin</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {user.created_at ? formatDistanceToNow(new Date(user.created_at), { addSuffix: true }) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(user.id)}
                            className="text-slate-300 hover:text-white"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          {!user.email_verified && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleVerifyEmail(user.id)}
                              className="text-green-400 hover:text-green-300"
                              title="Verify Email"
                            >
                              <MailCheck className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <p className="text-sm text-slate-400">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    className="border-slate-600"
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="border-slate-600"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Email Verified</Label>
                <Switch
                  checked={selectedUser.email_verified || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, email_verified: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Admin</Label>
                <Switch
                  checked={selectedUser.is_admin || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_admin: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Operator Admin (VBB)</Label>
                <Switch
                  checked={selectedUser.is_operator_admin || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_operator_admin: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Sysadmin</Label>
                <Switch
                  checked={selectedUser.is_sysadmin || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_sysadmin: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Beta User</Label>
                <Switch
                  checked={selectedUser.is_beta_user || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_beta_user: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Beta Membership Active</Label>
                <Switch
                  checked={selectedUser.beta_membership_active || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, beta_membership_active: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Platform Banned</Label>
                <Switch
                  checked={selectedUser.is_platform_banned || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_platform_banned: checked})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={() => handleUpdateUser(selectedUser.id, {
                email_verified: selectedUser.email_verified,
                is_admin: selectedUser.is_admin,
                is_operator_admin: selectedUser.is_operator_admin,
                is_sysadmin: selectedUser.is_sysadmin,
                is_beta_user: selectedUser.is_beta_user,
                beta_membership_active: selectedUser.beta_membership_active,
                is_platform_banned: selectedUser.is_platform_banned
              })}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== Patrons Management Panel ==============
const PatronsManagementPanel = () => {
  const [patrons, setPatrons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExamples, setShowExamples] = useState(true);
  const [editingPatron, setEditingPatron] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPatron, setNewPatron] = useState({
    name: '',
    type: 'business',
    tier: 'supporter',
    contribution_type: 'financial',
    visibility: 'public',
    status: 'active'
  });

  const fetchPatrons = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/patrons?include_examples=${showExamples}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPatrons(data);
      }
    } catch (error) {
      console.error('Error fetching patrons:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatrons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExamples]);

  const markAllExamples = async () => {
    try {
      const response = await fetch(`${API_URL}/api/patrons/mark-examples`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchPatrons();
      } else {
        toast.error('Failed to mark examples');
      }
    } catch (error) {
      toast.error('Error marking examples');
    }
  };

  const toggleExampleFlag = async (patronId, isExample) => {
    try {
      const response = await fetch(`${API_URL}/api/patrons/${patronId}/example?is_example=${isExample}`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(isExample ? 'Marked as example' : 'Unmarked as example');
        fetchPatrons();
      } else {
        toast.error('Failed to update');
      }
    } catch (error) {
      toast.error('Error updating patron');
    }
  };

  const deletePatron = async (patronId) => {
    if (!window.confirm('Are you sure you want to delete this patron?')) return;
    try {
      const response = await fetch(`${API_URL}/api/patrons/${patronId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Patron deleted');
        fetchPatrons();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Error deleting patron');
    }
  };

  const createPatron = async () => {
    try {
      const response = await fetch(`${API_URL}/api/patrons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newPatron)
      });
      if (response.ok) {
        toast.success('Patron created');
        setShowCreateDialog(false);
        setNewPatron({
          name: '',
          type: 'business',
          tier: 'supporter',
          contribution_type: 'financial',
          visibility: 'public',
          status: 'active'
        });
        fetchPatrons();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create patron');
      }
    } catch (error) {
      toast.error('Error creating patron');
    }
  };

  const tierColors = {
    'champion': 'bg-yellow-600',
    'partner': 'bg-purple-600',
    'supporter': 'bg-blue-600',
    'friend': 'bg-green-600'
  };

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Label className="text-slate-300 flex items-center gap-2">
                <Switch
                  checked={showExamples}
                  onCheckedChange={setShowExamples}
                />
                Show examples
              </Label>
              <Badge variant="outline" className="text-slate-400">
                {patrons.length} patrons
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={markAllExamples}
                className="border-amber-600 text-amber-400 hover:bg-amber-600/20"
              >
                <EyeOff className="w-4 h-4 mr-1" />
                Mark Seed Data
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCreateDialog(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Patron
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patrons Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Patrons & Partners
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Tier</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Example?</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patrons.map((patron) => (
                  <TableRow key={patron.id} className={`border-slate-700 ${patron.is_example ? 'opacity-60' : ''}`}>
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{patron.name}</p>
                        {patron.tagline && <p className="text-xs text-slate-400">{patron.tagline}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 capitalize">{patron.type}</TableCell>
                    <TableCell>
                      <Badge className={`${tierColors[patron.tier] || 'bg-slate-600'} text-white text-xs`}>
                        {patron.tier}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={patron.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                        {patron.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {patron.is_example ? (
                        <Badge variant="outline" className="border-amber-500 text-amber-400 text-xs">Example</Badge>
                      ) : (
                        <Badge className="bg-green-600 text-xs">Real</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleExampleFlag(patron.id, !patron.is_example)}
                          className="text-slate-300 hover:text-white"
                          title={patron.is_example ? 'Mark as real' : 'Mark as example'}
                        >
                          {patron.is_example ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deletePatron(patron.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Patron Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add New Patron</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a real patron or partner to display on the site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={newPatron.name}
                onChange={(e) => setNewPatron({...newPatron, name: e.target.value})}
                placeholder="Organization or person name"
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Type</Label>
                <select
                  value={newPatron.type}
                  onChange={(e) => setNewPatron({...newPatron, type: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                >
                  <option value="business">Business</option>
                  <option value="individual">Individual</option>
                  <option value="ngo">NGO</option>
                  <option value="foundation">Foundation</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300">Tier</Label>
                <select
                  value={newPatron.tier}
                  onChange={(e) => setNewPatron({...newPatron, tier: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                >
                  <option value="champion">Champion</option>
                  <option value="partner">Partner</option>
                  <option value="supporter">Supporter</option>
                  <option value="friend">Friend</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Tagline</Label>
              <Input
                value={newPatron.tagline || ''}
                onChange={(e) => setNewPatron({...newPatron, tagline: e.target.value})}
                placeholder="Short description"
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Website URL</Label>
              <Input
                value={newPatron.website_url || ''}
                onChange={(e) => setNewPatron({...newPatron, website_url: e.target.value})}
                placeholder="https://..."
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={createPatron}
              disabled={!newPatron.name}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Create Patron
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Tickets Panel Component
const TicketsPanel = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const token = localStorage.getItem('token');
        const statusFilter = filter !== 'all' ? `?status=${filter}` : '';
        const response = await fetch(`${API_URL}/api/contact/tickets/list${statusFilter}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTickets(data);
        }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
    };
    fetchTickets();
  }, [filter]);

  const refreshTickets = async () => {
    const token = localStorage.getItem('token');
    const statusFilter = filter !== 'all' ? `?status=${filter}` : '';
    const response = await fetch(`${API_URL}/api/contact/tickets/list${statusFilter}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      setTickets(await response.json());
    }
  };

  const updateStatus = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contact/tickets/${ticketId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(`Ticket marked as ${newStatus}`);
        refreshTickets();
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const priorityColors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/50',
    normal: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    low: 'bg-slate-500/20 text-slate-400 border-slate-500/50'
  };

  const statusColors = {
    open: 'bg-yellow-500/20 text-yellow-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    resolved: 'bg-green-500/20 text-green-400',
    closed: 'bg-slate-500/20 text-slate-400'
  };

  const categoryLabels = {
    sponsor: 'Sponsor',
    partner: 'Partner',
    advertiser: 'Advertiser',
    premium: 'Premium',
    ngo: 'NGO',
    ombudsman: 'Ombudsman',
    general: 'General'
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Ticket className="w-5 h-5 text-purple-400" />
            Contact Inquiries & Tickets
          </CardTitle>
          <div className="flex gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-slate-700 text-white border-slate-600 rounded-md px-3 py-1 text-sm"
            >
              <option value="all">All Tickets</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-slate-400">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="w-12 h-12 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-400">No tickets found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div 
                key={ticket.id} 
                className="bg-slate-700/50 rounded-lg p-4 border border-slate-600 hover:border-slate-500 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={priorityColors[ticket.priority] || priorityColors.normal}>
                        {ticket.priority}
                      </Badge>
                      <Badge className={statusColors[ticket.status] || statusColors.open}>
                        {ticket.status?.replace('_', ' ')}
                      </Badge>
                      <Badge variant="outline" className="text-slate-300 border-slate-500">
                        {categoryLabels[ticket.category] || ticket.category}
                      </Badge>
                    </div>
                    <h4 className="font-medium text-white truncate">{ticket.title}</h4>
                    <p className="text-sm text-slate-400 line-clamp-2 mt-1">{ticket.description}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {ticket.status === 'open' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-blue-400 border-blue-500/50 hover:bg-blue-500/20"
                        onClick={() => updateStatus(ticket.id, 'in_progress')}
                      >
                        Start
                      </Button>
                    )}
                    {ticket.status === 'in_progress' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-green-400 border-green-500/50 hover:bg-green-500/20"
                        onClick={() => updateStatus(ticket.id, 'resolved')}
                      >
                        Resolve
                      </Button>
                    )}
                    {ticket.status !== 'closed' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-slate-400 border-slate-500/50 hover:bg-slate-500/20"
                        onClick={() => updateStatus(ticket.id, 'closed')}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Club status configurations
const CLUB_STATUS = {
  active: { label: 'Active', color: 'bg-green-500', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-yellow-500', icon: Clock },
  suspended: { label: 'Suspended', color: 'bg-red-500', icon: AlertTriangle },
  trial: { label: 'Trial', color: 'bg-blue-500', icon: Clock },
};

const VBBOperationsCenter = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubDetailOpen, setClubDetailOpen] = useState(false);

  // Access control - VBB operators only
  const isVBBOperator = user?.is_operator_admin || user?.email?.endsWith('@verybigbox.com');

  useEffect(() => {
    if (!isVBBOperator) {
      toast.error('Access denied');
      navigate('/');
      return;
    }
    fetchData();
  }, [isVBBOperator, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch platform-wide stats
      const [statsRes, clubsRes] = await Promise.all([
        fetch(`${API_URL}/api/vbb/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/vbb/clubs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      } else {
        // Use mock data for now
        setStats({
          total_clubs: 1,
          total_users: 156,
          total_members: 124,
          total_revenue_mtd: 868,
          total_revenue_ytd: 4340,
          vbb_revenue_mtd: 86.80,
          vbb_revenue_ytd: 434,
          active_clubs: 1,
          pending_clubs: 0,
          support_tickets_open: 3,
          uptime_percent: 99.8,
          api_calls_today: 12450,
          storage_used_gb: 2.4
        });
      }

      if (clubsRes.ok) {
        setClubs(await clubsRes.json());
      } else {
        // Use mock data
        setClubs([
          {
            id: 'aadc-001',
            name: 'All American Dog Club',
            domain: 'allamericandog.club',
            status: 'active',
            country: 'US',
            members: 124,
            revenue_mtd: 868,
            created_at: '2024-06-15',
            last_active: new Date().toISOString(),
            contact_email: 'admin@allamericandog.club',
            plan: 'standard'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredClubs = clubs.filter(club => 
    club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    club.domain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isVBBOperator) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">VBB Operations Center is only accessible to authorized VBB personnel.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800" data-testid="vbb-operations-center">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-900 border-b border-purple-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">World Pets Operations</h1>
                <p className="text-purple-200 text-sm">worldpets.club - Global Pet Club Platform</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-green-400 border-green-400">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
                All Systems Operational
              </Badge>
              <Link to="/sysadmin">
                <Button variant="outline" size="sm" className="gap-2 text-white border-white/30 hover:bg-white/10">
                  <Settings className="w-4 h-4" />
                  Sysadmin
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: 'Total Clubs', value: stats?.total_clubs || 0, icon: Building2, color: 'text-purple-400' },
            { label: 'Total Users', value: stats?.total_users?.toLocaleString() || 0, icon: Users, color: 'text-blue-400' },
            { label: 'Members', value: stats?.total_members?.toLocaleString() || 0, icon: Users, color: 'text-green-400' },
            { label: 'Revenue MTD', value: `$${(stats?.total_revenue_mtd || 0).toLocaleString()}`, icon: DollarSign, color: 'text-emerald-400' },
            { label: 'VBB Revenue MTD', value: `$${(stats?.vbb_revenue_mtd || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-amber-400' },
            { label: 'Uptime', value: `${stats?.uptime_percent || 99.9}%`, icon: Activity, color: 'text-cyan-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <Icon className={`w-5 h-5 ${color}`} />
                  <span className="text-xs text-slate-400">{label}</span>
                </div>
                <p className="text-2xl font-bold text-white mt-2">{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="clubs" className="data-[state=active]:bg-purple-600">
              <Globe className="w-4 h-4 mr-2" />
              Clubs
            </TabsTrigger>
            <TabsTrigger value="infrastructure" className="data-[state=active]:bg-purple-600">
              <Server className="w-4 h-4 mr-2" />
              Infrastructure
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-purple-600">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-purple-600">
              <HelpCircle className="w-4 h-4 mr-2" />
              Support
            </TabsTrigger>
            <TabsTrigger value="roadmap" className="data-[state=active]:bg-purple-600">
              <Target className="w-4 h-4 mr-2" />
              Roadmap
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-purple-600">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="projections" className="data-[state=active]:bg-purple-600">
              <TrendingUp className="w-4 h-4 mr-2" />
              Ad Revenue
            </TabsTrigger>
            <TabsTrigger value="tickets" className="data-[state=active]:bg-purple-600">
              <Ticket className="w-4 h-4 mr-2" />
              Tickets
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-purple-600">
              <UserCog className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="patrons" className="data-[state=active]:bg-purple-600">
              <Heart className="w-4 h-4 mr-2" />
              Patrons
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Revenue Chart Placeholder */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Revenue Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48 flex items-center justify-center border border-dashed border-slate-600 rounded-lg">
                    <div className="text-center text-slate-400">
                      <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Revenue chart coming soon</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-xs text-slate-400">YTD Revenue</p>
                      <p className="text-xl font-bold text-white">${(stats?.total_revenue_ytd || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-xs text-slate-400">VBB Share (10%)</p>
                      <p className="text-xl font-bold text-emerald-400">${(stats?.vbb_revenue_ytd || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-purple-400" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-slate-600 hover:bg-slate-700">
                    <Plus className="w-5 h-5" />
                    <span className="text-xs">New Club</span>
                  </Button>
                  <Link to="/sysadmin">
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-slate-600 hover:bg-slate-700 w-full">
                      <Users className="w-5 h-5" />
                      <span className="text-xs">User Admin</span>
                    </Button>
                  </Link>
                  <Link to="/about/service-agreement">
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-slate-600 hover:bg-slate-700 w-full">
                      <FileText className="w-5 h-5" />
                      <span className="text-xs">Agreements</span>
                    </Button>
                  </Link>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-slate-600 hover:bg-slate-700">
                    <Shield className="w-5 h-5" />
                    <span className="text-xs">Security</span>
                  </Button>
                </CardContent>
              </Card>

              {/* Active Clubs */}
              <Card className="bg-slate-800/50 border-slate-700 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-blue-400" />
                      Active Clubs
                    </span>
                    <Button size="sm" variant="outline" className="border-slate-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Club
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Club</TableHead>
                        <TableHead className="text-slate-400">Domain</TableHead>
                        <TableHead className="text-slate-400">Region</TableHead>
                        <TableHead className="text-slate-400">Members</TableHead>
                        <TableHead className="text-slate-400">Revenue MTD</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clubs.map(club => (
                        <TableRow key={club.id} className="border-slate-700">
                          <TableCell className="text-white font-medium">{club.name}</TableCell>
                          <TableCell>
                            <a href={`https://${club.domain}`} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-400 hover:underline flex items-center gap-1">
                              {club.domain}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </TableCell>
                          <TableCell className="text-slate-300">{club.country}</TableCell>
                          <TableCell className="text-slate-300">{club.members?.toLocaleString()}</TableCell>
                          <TableCell className="text-emerald-400">${club.revenue_mtd?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge className={`${CLUB_STATUS[club.status]?.color} text-white`}>
                              {CLUB_STATUS[club.status]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => {
                              setSelectedClub(club);
                              setClubDetailOpen(true);
                            }}>
                              <Settings className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clubs Tab */}
          <TabsContent value="clubs" className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Club Management</CardTitle>
                  <div className="flex gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Search clubs..." 
                        className="pl-10 bg-slate-700 border-slate-600 text-white w-64"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Button className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Onboard New Club
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Club Name</TableHead>
                      <TableHead className="text-slate-400">Domain</TableHead>
                      <TableHead className="text-slate-400">Region</TableHead>
                      <TableHead className="text-slate-400">Members</TableHead>
                      <TableHead className="text-slate-400">Revenue MTD</TableHead>
                      <TableHead className="text-slate-400">VBB Fee</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Created</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClubs.map(club => (
                      <TableRow key={club.id} className="border-slate-700 hover:bg-slate-700/50">
                        <TableCell className="text-white font-medium">{club.name}</TableCell>
                        <TableCell>
                          <a href={`https://${club.domain}`} target="_blank" rel="noopener noreferrer" 
                             className="text-blue-400 hover:underline flex items-center gap-1">
                            {club.domain}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </TableCell>
                        <TableCell className="text-slate-300">{club.country}</TableCell>
                        <TableCell className="text-slate-300">{club.members?.toLocaleString()}</TableCell>
                        <TableCell className="text-emerald-400">${club.revenue_mtd?.toLocaleString()}</TableCell>
                        <TableCell className="text-amber-400">${((club.revenue_mtd || 0) * 0.1).toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge className={`${CLUB_STATUS[club.status]?.color} text-white`}>
                            {CLUB_STATUS[club.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {new Date(club.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white">
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Infrastructure Tab */}
          <TabsContent value="infrastructure" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Server className="w-5 h-5 text-cyan-400" />
                    Servers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-green-900/30 border border-green-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-green-400">Primary (US-East)</span>
                      <Badge className="bg-green-500">Healthy</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">CPU: 24% | Memory: 61%</p>
                  </div>
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">CDN (Global)</span>
                      <Badge className="bg-green-500">Active</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">45 edge locations</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Database className="w-5 h-5 text-purple-400" />
                    Database
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-green-900/30 border border-green-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-green-400">MongoDB Atlas</span>
                      <Badge className="bg-green-500">Connected</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">M10 Cluster | 3 Nodes</p>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Storage Used</span>
                    <span className="text-white">{stats?.storage_used_gb || 0} GB</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Connections</span>
                    <span className="text-white">23 / 500</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-400" />
                    API Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Requests Today</span>
                    <span className="text-white">{(stats?.api_calls_today || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Avg Response</span>
                    <span className="text-green-400">89ms</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Error Rate</span>
                    <span className="text-green-400">0.02%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Uptime (30d)</span>
                    <span className="text-green-400">{stats?.uptime_percent || 99.9}%</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Revenue & Billing</CardTitle>
                <CardDescription className="text-slate-400">
                  VBB receives 10% of gross revenues from all clubs, which includes payment processing fees.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <p className="text-xs text-slate-400">Total Revenue MTD</p>
                    <p className="text-2xl font-bold text-white">${(stats?.total_revenue_mtd || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-emerald-900/30 border border-emerald-700 rounded-lg">
                    <p className="text-xs text-emerald-400">VBB Share (10%)</p>
                    <p className="text-2xl font-bold text-emerald-400">${(stats?.vbb_revenue_mtd || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <p className="text-xs text-slate-400">Payment Processing</p>
                    <p className="text-2xl font-bold text-white">~${((stats?.total_revenue_mtd || 0) * 0.029).toFixed(0)}</p>
                  </div>
                  <div className="p-4 bg-amber-900/30 border border-amber-700 rounded-lg">
                    <p className="text-xs text-amber-400">Net to VBB</p>
                    <p className="text-2xl font-bold text-amber-400">
                      ${((stats?.vbb_revenue_mtd || 0) - ((stats?.total_revenue_mtd || 0) * 0.029)).toFixed(0)}
                    </p>
                  </div>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Club</TableHead>
                      <TableHead className="text-slate-400">Gross Revenue</TableHead>
                      <TableHead className="text-slate-400">VBB Fee (10%)</TableHead>
                      <TableHead className="text-slate-400">Payment Processing</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clubs.map(club => (
                      <TableRow key={club.id} className="border-slate-700">
                        <TableCell className="text-white">{club.name}</TableCell>
                        <TableCell className="text-white">${(club.revenue_mtd || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-emerald-400">${((club.revenue_mtd || 0) * 0.1).toFixed(2)}</TableCell>
                        <TableCell className="text-slate-400">${((club.revenue_mtd || 0) * 0.029).toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-500">Full</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-6">
            <SupportInbox darkMode={true} />
          </TabsContent>

          {/* Roadmap Tab */}
          <TabsContent value="roadmap" className="space-y-6">
            <OperationsRoadmap token={token} />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            {/* Development History Section */}
            <DevelopmentHistory />
            
            {/* VBB Documents Section */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">VBB Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <Link to="/about/service-agreement">
                    <div className="p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-purple-400" />
                        <div>
                          <p className="font-medium text-white">Service Agreement Template</p>
                          <p className="text-sm text-slate-400">Master agreement between VBB and clubs</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <Link to="/about/story">
                    <div className="p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-amber-400" />
                        <div>
                          <p className="font-medium text-white">Club Story</p>
                          <p className="text-sm text-slate-400">History and vision of the platform</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-green-400" />
                      <div>
                        <p className="font-medium text-white">Cost Analysis</p>
                        <p className="text-sm text-slate-400">Operating cost projections (10K-10M users)</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-blue-400" />
                      <div>
                        <p className="font-medium text-white">International Strategy</p>
                        <p className="text-sm text-slate-400">TLD patterns and expansion roadmap</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ad Revenue Projections Tab */}
          <TabsContent value="projections" className="space-y-6">
            <AdRevenueProjections />
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets" className="space-y-6">
            <TicketsPanel />
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <UserManagementPanel />
          </TabsContent>

          <TabsContent value="patrons" className="space-y-6">
            <PatronsManagementPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Club Detail Dialog */}
      <Dialog open={clubDetailOpen} onOpenChange={setClubDetailOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedClub?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Club management and configuration
            </DialogDescription>
          </DialogHeader>
          {selectedClub && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Domain</p>
                  <p className="text-white">{selectedClub.domain}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge className={`${CLUB_STATUS[selectedClub.status]?.color} text-white`}>
                    {CLUB_STATUS[selectedClub.status]?.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Members</p>
                  <p className="text-white">{selectedClub.members?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Revenue MTD</p>
                  <p className="text-emerald-400">${selectedClub.revenue_mtd?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClubDetailOpen(false)}>Close</Button>
            <Button className="bg-purple-600 hover:bg-purple-700">Edit Club</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VBBOperationsCenter;
